import { useState, useEffect, useRef } from 'react';
import { documentService, Document, CreateDocumentData } from '../../services/document.service';
import { projectService, Project } from '../../services/project.service';
import { useAuth } from '../../contexts/AuthContext';
import { documentAdminService } from '../../services/document-admin.service';
import { SignatureInfoModal } from '../../components/documents/SignatureInfoModal';

const DOCUMENT_TYPES = [
  { value: 'plano', label: '📐 Plano', badge: 'badge-primary' },
  { value: 'contrato', label: '📄 Contrato', badge: 'badge-success' },
  { value: 'permiso', label: '📜 Permiso', badge: 'badge-warning' },
  { value: 'factura', label: '🧾 Factura', badge: 'badge-neutral' },
  { value: 'reporte', label: '📊 Reporte', badge: 'badge-neutral' },
  { value: 'otro', label: '📎 Otro', badge: 'badge-neutral' },
];

const DOCUMENT_CATEGORIES = [
  'Arquitectónico', 'Estructural', 'Eléctrico', 'Hidrosanitario',
  'Legal', 'Financiero', 'Seguridad', 'Calidad', 'Otro',
];

const INITIAL_FORM: CreateDocumentData = {
  project_id: '',
  name: '',
  type: 'otro',
  category: 'Otro',
  file: null as any,
};

export function DocumentsPage() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [signatureModal, setSignatureModal] = useState<{ id: string; name: string } | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateDocumentData>(INITIAL_FORM);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [documentsData, projectsData] = await Promise.all([
        documentService.getDocuments(selectedProject === 'all' ? undefined : selectedProject),
        projectService.getProjects(),
      ]);
      setDocuments(documentsData);
      setProjects(projectsData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar documentos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Debes seleccionar un archivo');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await documentService.createDocument({
        ...formData,
        file: selectedFile,
      });
      await loadData();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al subir documento');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este documento?')) return;
    try {
      await documentService.deleteDocument(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar documento');
    }
  };

  const handleSign = async (id: string) => {
    try {
      await documentService.signDocument(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al firmar documento');
    }
  };

  const handleView = async (doc: Document) => {
    try {
      setDownloadingId(doc.id);
      const { url } = await documentService.getDownloadUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.message || 'Error al abrir documento');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      setDownloadingId(doc.id);
      const { url } = await documentService.getDownloadUrl(doc.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      setError(err.message || 'Error al descargar documento');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData(INITIAL_FORM);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({ ...prev, name: file.name }));
    }
  };

  const getTypeInfo = (type: string) => {
    return DOCUMENT_TYPES.find(t => t.value === type) || DOCUMENT_TYPES[5];
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : projectId.slice(0, 8);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      doc.name.toLowerCase().includes(term) ||
      (doc.project_name && doc.project_name.toLowerCase().includes(term));
    const matchesType = filterType === 'all' || doc.type === filterType;
    return matchesSearch && matchesType;
  });

  const activeFiltersCount =
    (selectedProject !== 'all' ? 1 : 0) + (filterType !== 'all' ? 1 : 0);

  const handleToggleRequiresSignature = async (doc: Document, value: boolean) => {
    if (doc.signed) return;
    setTogglingId(doc.id);
    try {
      await documentAdminService.toggleRequiresSignature(doc.id, value);
      await loadData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Documentos</h1>
          <p className="subtitle">Gestión de documentos de proyectos</p>
        </div>
        <div className="actions">
          <button onClick={loadData} className="btn btn-ghost">
            🔄 Actualizar
          </button>
          <button
            onClick={() => {
              setFormData(INITIAL_FORM);
              setSelectedFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            + Subir Documento
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-search">
          <span className="toolbar-search-icon">🔍</span>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar documento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-filters">
          <select
            className="form-select"
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setTimeout(loadData, 0);
            }}
          >
            <option value="all">Todos los proyectos</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>

          <select
            className="form-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Todos los tipos</option>
            {DOCUMENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          {activeFiltersCount > 0 && (
            <span className="filter-badge">
              {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''} activo{activeFiltersCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Contenido */}
      {isLoading && documents.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando documentos...</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {documents.length === 0 ? '📄' : '🔍'}
          </div>
          <h3 className="empty-state-title">
            {documents.length === 0 ? 'No hay documentos registrados' : 'Sin resultados'}
          </h3>
          <p className="empty-state-text">
            {documents.length === 0
              ? 'Sube el primer documento para comenzar.'
              : 'No se encontraron documentos con los filtros aplicados.'}
          </p>
          {documents.length === 0 && (
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              + Subir primer documento
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map((doc) => {
            const typeInfo = getTypeInfo(doc.type);
            const isBusy = downloadingId === doc.id;
            const requiresSignature = doc.requires_signature === 1;
            const isToggling = togglingId === doc.id;
            return (
              <div key={doc.id} className="card" style={{ padding: '18px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'var(--primary-light)',
                      color: 'var(--primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      flexShrink: 0,
                    }}>
                      {typeInfo.label.split(' ')[0]}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#0f172a',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }} title={doc.name}>
                        {doc.name}
                      </h4>
                      <p style={{ fontSize: '12px', color: 'var(--gray)', margin: '2px 0 0 0' }}>
                        {getProjectName(doc.project_id)}
                      </p>
                    </div>
                  </div>
                  <span className={`badge ${typeInfo.badge}`}>{typeInfo.label}</span>
                </div>

                {/* Info */}
                <div className="info-row">
                  <span className="info-row-label">Categoría</span>
                  <span className="info-row-value">{doc.category}</span>
                </div>
                <div className="info-row">
                  <span className="info-row-label">Tamaño</span>
                  <span className="info-row-value">{formatFileSize(doc.size)}</span>
                </div>
                <div className="info-row">
                  <span className="info-row-label">Subido</span>
                  <span className="info-row-value">{formatDate(doc.created_at)}</span>
                </div>
                <div className="info-row">
                  <span className="info-row-label">Firma</span>
                  <span className={`badge ${doc.signed ? 'badge-success' : requiresSignature ? 'badge-warning' : 'badge-neutral'}`}>
                    {doc.signed ? '✅ Firmado' : requiresSignature ? '⏳ Requiere firma' : '— No requiere'}
                  </span>
                </div>

                {/* Toggle requiere firma (solo si no está firmado) */}
                {!doc.signed && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '10px',
                      padding: '8px 10px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      cursor: isToggling ? 'wait' : 'pointer',
                      fontSize: '13px',
                      color: '#1e293b',
                      opacity: isToggling ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requiresSignature}
                      disabled={isToggling}
                      onChange={(e) => handleToggleRequiresSignature(doc, e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: 'var(--primary)', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 500 }}>Requiere firma del cliente</span>
                  </label>
                )}

                {/* Ver firma (si ya está firmado) */}
                {doc.signed && (
                  <button
                    onClick={() => setSignatureModal({ id: doc.id, name: doc.name })}
                    style={{
                      marginTop: '10px',
                      width: '100%',
                      padding: '8px',
                      background: 'var(--primary-light)',
                      color: 'var(--primary)',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    ✍️ Ver firma registrada
                  </button>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--light-gray)', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleView(doc)}
                    disabled={isBusy}
                    className="btn-action-view"
                  >
                    {isBusy ? '⏳' : '👁️'} Ver
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    disabled={isBusy}
                    className="btn-action-download"
                  >
                    {isBusy ? '⏳' : '⬇️'} Descargar
                  </button>
                  {!doc.signed && user?.role === 'client' && (
                    <button onClick={() => handleSign(doc.id)} className="btn-action-sign">
                      ✍️ Firmar
                    </button>
                  )}
                  {(user?.role === 'sys_admin' || user?.role === 'general_manager') && (
                    <button onClick={() => handleDelete(doc.id)} className="btn-action-delete">
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal subir */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📤 Subir Documento</h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Proyecto</label>
                    <select
                      className="form-select"
                      required
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    >
                      <option value="">Seleccionar proyecto...</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Nombre del documento</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre del documento"
                    />
                  </div>
                </div>

                <div className="form-row cols-2">
                  <div className="form-col">
                    <label className="form-label required">Tipo</label>
                    <select
                      className="form-select"
                      required
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as Document['type'] })}
                    >
                      {DOCUMENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-col">
                    <label className="form-label required">Categoría</label>
                    <select
                      className="form-select"
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label required">Archivo</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      required
                      onChange={handleFileChange}
                      className="form-input"
                      style={{ padding: '8px' }}
                    />
                    {selectedFile && (
                      <span className="form-help">
                        Archivo seleccionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </span>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Subiendo...' : 'Subir Documento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de firma (auditoría) */}
      {signatureModal && (
        <SignatureInfoModal
          documentId={signatureModal.id}
          documentName={signatureModal.name}
          onClose={() => setSignatureModal(null)}
        />
      )}
    </div>
  );
}